import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, CardHeader, Avatar, Typography,
  TextField, Button, Grid, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import axios from 'axios';
import mqtt from 'mqtt';

export default function UpdateZone() {

  const { state } = useLocation();
  const navigate = useNavigate();
  const mqttClient = useRef(null);

  const zona = state || {};

  const {
    id,
    companyid,
    name,
    description,
    type,
    classification,
    region,
    populationDensity,
    isMonitored,
    lightingmodeId
  } = zona;

  const [zoneName, setZoneName] = useState(name || '');
  const [zoneDescription, setZoneDescription] = useState(description || '');
  const [zoneType, setZoneType] = useState(type || '');
  const [zoneClassification, setZoneClassification] = useState(classification || '');
  const [zoneRegion, setZoneRegion] = useState(region || '');
  const [zonePopulationDensity, setZonePopulationDensity] = useState(populationDensity || '');
  const [zoneIsMonitored, setZoneIsMonitored] = useState(isMonitored || false);
  const [zoneLightingMode, setZoneLightingMode] = useState(lightingmodeId || '');
  const [lightingModes, setLightingModes] = useState([]);

  const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER_URL;

  useEffect(() => {
    mqttClient.current = mqtt.connect('wss://smartlight.bhtelecom.ba:8083');

    mqttClient.current.on('connect', () => {
      console.log('Povezan na MQTT broker');
    });

    mqttClient.current.on('error', (err) => {
      console.error('MQTT error', err);
    });

    return () => mqttClient.current?.end();
  }, []);

  useEffect(() => {
    axios
      .get(`${SERVER_URL}/api/lightingmodes`)
      .then(res => setLightingModes(res.data))
      .catch(err => console.error('Ne mogu dohvatiti lighting modes', err));
  }, []);

  const notifyLightingModeChanged = (zoneid) => {
    if (!mqttClient.current) return;

    const payload = {
      zoneid,
      type: 'LightingModeChangedforZone',
      timestamp: Date.now()
    };

    mqttClient.current.publish(
      '/smartmap/LightingModeChanged',
      JSON.stringify(payload),
      { qos: 1 }
    );
  };

  const updateAPIData = () => {
    axios.put(`${SERVER_URL}/api/zonas/${id}`, {
      companyid,
      name: zoneName,
      description: zoneDescription,
      type: zoneType,
      classification: zoneClassification,
      region: zoneRegion,
      populationDensity: zonePopulationDensity ? Number(zonePopulationDensity) : 0,
      isMonitored: zoneIsMonitored,
      lightingmodeid: zoneLightingMode
    })
      .then(() => {
        notifyLightingModeChanged(id);
        navigate(-1);
      })
      .catch(err => console.error(err));
  };

  // 🔥 DARK SELECT CONFIG (jednom definisano)
  const darkSelect = {
    style: { color: '#ff9800' },
    MenuProps: {
      PaperProps: {
        sx: {
          bgcolor: '#0f172a',
          color: '#ff9800',
          '& .MuiMenuItem-root': {
            '&:hover': {
              bgcolor: '#1e293b'
            },
            '&.Mui-selected': {
              bgcolor: '#1e40af'
            }
          }
        }
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#001536', width: '100%', padding: '20px', minHeight: '100vh' }}>

      <Grid container justifyContent="center">
        <Card sx={{ maxWidth: 600, width: '100%', backgroundColor: '#00122E', color: '#ff9800', borderRadius: 2, boxShadow: 3 }}>

          <CardHeader
            avatar={<Avatar sx={{ width: 60, height: 60 }}>{name?.[0] || "Z"}</Avatar>}
            title={<Typography variant="h6" sx={{ color: '#ff9800' }}>{name}</Typography>}
            subheader={<Typography variant="body2" sx={{ color: '#ff9800' }}>{region}</Typography>}
          />

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>

                <TextField
                  label="Naziv zone"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ style: { color: '#ff9800' } }}
                  InputProps={{ style: { color: '#ff9800' } }}
                />

                <TextField
                  label="Opis"
                  value={zoneDescription}
                  onChange={(e) => setZoneDescription(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ style: { color: '#ff9800' } }}
                  InputProps={{ style: { color: '#ff9800' } }}
                />

                <TextField
                  select
                  label="Tip"
                  value={zoneType}
                  onChange={(e) => setZoneType(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    style: { color: '#ff9800' }
                  }}
                  SelectProps={{
                    ...darkSelect,
                    sx: {
                      color: '#ff9800'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ff9800',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ffa726',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff9800',
                      },
                    },

                    '& .MuiInputLabel-root': {
                      color: '#ff9800',
                    },

                    '& .MuiSvgIcon-root': {
                      color: '#ff9800',
                    },

                    // 🔥 KLJUČNO — tekst u selectu
                    '& .MuiSelect-select': {
                      color: '#ff9800',
                    },
                  }}
                >
                  <MenuItem sx={{ color: '#ff9800' }} value="Stambena">Stambena</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="Industrijska">Industrijska</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="Mješovita">Mješovita</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="Poljoprivredna">Poljoprivredna</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="Poslovna">Poslovna</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Klasifikacija"
                  value={zoneClassification}
                  onChange={(e) => setZoneClassification(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ style: { color: '#ff9800' } }}
                  SelectProps={{
                    ...darkSelect,
                    sx: {
                      color: '#ff9800'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ff9800',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ffa726',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff9800',
                      },
                    },

                    '& .MuiInputLabel-root': {
                      color: '#ff9800',
                    },

                    '& .MuiSvgIcon-root': {
                      color: '#ff9800',
                    },

                    // 🔥 KLJUČNO — tekst u selectu
                    '& .MuiSelect-select': {
                      color: '#ff9800',
                    },
                  }}
                >
                  <MenuItem sx={{ color: '#ff9800' }} value="Urban">Urban</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="suburban">Suburban</MenuItem>
                  <MenuItem sx={{ color: '#ff9800' }} value="Rural">Rural</MenuItem>
                </TextField>

                <TextField
                  label="Regija"
                  value={zoneRegion}
                  onChange={(e) => setZoneRegion(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ style: { color: '#ff9800' } }}
                  InputProps={{ style: { color: '#ff9800' } }}
                />

                <TextField
                  select
                  label="Lighting režim"
                  value={zoneLightingMode}
                  onChange={(e) => setZoneLightingMode(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{ style: { color: '#ff9800' } }}
                  SelectProps={{
                    ...darkSelect,
                    sx: {
                      color: '#ff9800'
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ff9800',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ffa726',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff9800',
                      },
                    },

                    '& .MuiInputLabel-root': {
                      color: '#ff9800',
                    },

                    '& .MuiSvgIcon-root': {
                      color: '#ff9800',
                    },

                    // 🔥 KLJUČNO — tekst u selectu
                    '& .MuiSelect-select': {
                      color: '#ff9800',
                    },
                  }}
                >
                  {lightingModes.map((mode) => (
                    <MenuItem sx={{ color: '#ff9800' }} key={mode.id} value={mode.id}>
                      {mode.name}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControlLabel
                  control={
                    <Switch
                      checked={zoneIsMonitored}
                      onChange={(e) => setZoneIsMonitored(e.target.checked)}
                      sx={{ color: '#ff9800' }}
                    />
                  }
                  label={<Typography sx={{ color: '#ff9800' }}>Monitorirana zona</Typography>}
                />

              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={() => navigate(-1)}
              >
                Odustani
              </Button>

              <Button
                sx={{
                  minWidth: 100,
                  backgroundColor: '#001B44',
                  color: '#ff9800',                // 🔥 tekst orange
                  border: '1px solid #ff9800',     // 🔥 border orange
                  '&:hover': {
                    backgroundColor: '#001B44',
                    border: '1px solid #ffa726',   // 🔥 malo svjetliji hover
                    color: '#ffa726'
                  }
                }}
                onClick={updateAPIData}
              >
                Spasi
              </Button>
            </Box>

          </CardContent>
        </Card>
      </Grid>
    </div>
  );
}